import React, { useMemo, useState } from "react";
import {
    Box,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { tokens } from "../../../assets/theme";
import messages from "./messages";
import { FormattedMessage, useIntl } from "react-intl";

export default function PlannedCalendarGrid(props) {
    const { rows, isLoading, onShowDetails } = props;

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();

    const language = localStorage.getItem("language") || "en";
    const translations = language === "sv" ? svSE : enUS;

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const columnOrderLocalStorageName = "calendarExceptions-columnOrder";
    const columnWidthsLocalStorageName = "calendarExceptions-columnWidths";

    const reorderColumns = (orderArray, columnsToFix) => {
        const reordered = orderArray
            .map((field) =>
                columnsToFix.find((col) => col.field === field)
            )
            .filter(Boolean);
        const remaining = columnsToFix.filter(
            (col) => !reordered.includes(col)
        );
        return [...reordered, ...remaining];
    };

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const applyColumnWidths = (widthMap, columnsToFix) =>
        columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));

    const initColumnsFromLocalStorage = (cols) => {
        let out = cols;
        const savedOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedOrder) out = reorderColumns(JSON.parse(savedOrder), out);
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) out = applyColumnWidths(JSON.parse(savedWidths), out);
        return out;
    };

    const baseColumns = useMemo(
        () => [
            {
                field: "date",
                headerName: formatMessage(messages.date),
                flex: 1,
                valueGetter: (params) =>
                    dayjs(params.row.date).format("YYYY-MM-DD"),
            },
            {
                field: "name",
                headerName: formatMessage(messages.name),
                flex: 1,
            },
            {
                field: "comment",
                headerName: formatMessage(messages.comment),
                flex: 1,
            },
            {
                field: "details",
                headerName: "",
                width: 80,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Tooltip title={formatMessage(messages.showDetails)}>
                        <IconButton onClick={() => props.openEdit(params.row)} size="small">
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => props.remove(params.row.id)} size="small">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [colors, onShowDetails]
    );

    const columns = useMemo(
        () => initColumnsFromLocalStorage(baseColumns),
        [baseColumns]
    );

    const handleColumnOrderChange = () => {
        if (!apiRef.current) return;
        const fields = apiRef.current
            .getVisibleColumns()
            .map((c) => c.field);
        localStorage.setItem(
            columnOrderLocalStorageName,
            JSON.stringify(fields)
        );
    };

    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const map =
            JSON.parse(
                localStorage.getItem(columnWidthsLocalStorageName)
            ) || {};
        map[colDef.field] = width;
        localStorage.setItem(
            columnWidthsLocalStorageName,
            JSON.stringify(map)
        );
    };

    if (isLoading) {
        return (
            <Box padding={2}>
                <Box sx={{ position: "relative", borderRadius: "4px", backgroundColor: workspaceBg, }}>
                    <CircularProgress color="secondary" />
                </Box>
            </Box>
        );
    }

    // Lägg till id om backend inte skickar något
    const rowsWithId =
        (rows || []).map((row, index) => ({
            id: row.id ?? index,
            ...row,
        })) || [];

    return (
        <Box padding={2} paddingTop={0}>
            <Paper sx={{ position: "relative", borderRadius: "4px" }}>
                <Box
                    m="0px 0 0 0"
                    height="70vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: "1.0rem",
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px",
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton":
                        {
                            opacity: 0,
                            transition: "opacity 0.2s",
                        },
                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton":
                            { opacity: 1 },
                    }}
                >
                    <DataGridPro
                        apiRef={apiRef}
                        rows={rowsWithId}
                        columns={columns}
                        localeText={
                            translations.components.MuiDataGrid.defaultProps
                                .localeText
                        }
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnOrderChange}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(m) =>
                            setColumnVisibilityModel(m)
                        }
                        slotProps={{
                            panel: {
                                sx: {
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                        backgroundColor: "#799c4a",
                                        opacity: 0.3,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb":
                                    {
                                        backgroundColor: "#799c4a",
                                    },
                                },
                            },
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
